import React, {forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState} from "react";
import {ApolloClient, DocumentNode, useQuery} from "@apollo/client";
import {
	Alert,
	Box, IconButton, LinearProgress,
} from "@mui/material";
import {
	Refresh,
	ArrowBack,

} from "@mui/icons-material";
import {translateFunction} from "../../../localization/TranslationService";
import {
	DataGridProProps,
	GridColDef,
	GridValidRowModel,
	GridFilterModel, GridSortModel, GridPaginationModel,
	nlNL, GridToolbarExport
} from "@mui/x-data-grid-pro";
import {GridValueGetterParams} from "@mui/x-data-grid/models/params/gridCellParams";
import {GraphQlDataGridFilter} from "./GraphQlDataGridFilter";
import {GlobalState} from "../../../global/GlobalState";
import {StripedDataGrid} from "./StripedDataGrid";
import {localStorageOfObject} from "../../../libraries/util/LocalStorage";
import {GridFilterItem} from "@mui/x-data-grid/models/gridFilterItem";

const translate = translateFunction();

export interface IGraphQlDataGridOptions {
	rowsPerPageOptions: number[];
}

export type GraphQlGridColDef<R extends GridValidRowModel = any, V = any, F = V> = GridColDef<R, V, F> & {
	filterAction?: (params: any) => any;
	filterIgnoreCase?: boolean;

};

function unflattenObject(flattenedObj: { [key: string]: any }): { [key: string]: any } {
	const unflattenedObj: { [key: string]: any } = {};

	for (const key in flattenedObj) {
		if (!Object.prototype.hasOwnProperty.call(flattenedObj, key)) continue;

		const keys: string[] = key.split(".");
		let currentObj = unflattenedObj;

		for (let i = 0; i < keys.length - 1; i++) {
			const currentKey = keys[i];
			currentObj = currentObj[currentKey] = currentObj[currentKey] || {};
		}

		currentObj[keys[keys.length - 1]] = flattenedObj[key];
	}

	return unflattenedObj;
}

// The date-column filter picker emits a bare calendar date ("2026-05-01"). Some backends use the
// strict HotChocolate DateTime scalar (DateTimeOperationFilterInput), which rejects a bare date with
// "DateTime cannot parse the given literal" (400). Promote it to a full ISO-8601 instant at UTC
// midnight, which every backend accepts - and which is the same instant the lenient backends already
// derived from the bare date, so their results are unchanged.
function toGraphQlDateTime(value: any): any {
	if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
		return `${value}T00:00:00.000Z`;
	}
	return value;
}

class GraphQlDataGridState {
	static default(props: GraphQlDataGridProps, filterItems?: GridFilterItem[], sortModel?: GridSortModel ): GraphQlDataGridState {
		const filterModel =  props.defaultFilterModel ?? {items: []};
		if (filterItems != null) filterModel.items = filterItems?? [];
		return new GraphQlDataGridState(
			{
				rowsPerPageOptions: props.rowsPerPageOptions,
			},
			props.initialPageSize,
			0,
			0,
			null,
			null,
			null,
			null,
			filterModel,
			(sortModel ??  (props.defaultSortModel ?? null)),
			props.columns,
			false,
			undefined
		);
	}

	constructor(
		readonly options: IGraphQlDataGridOptions,
		readonly pageSize: number,
		readonly currentPage: number,
		readonly offsetPage: number,
		readonly afterCursor: string | null,
		readonly beforeCursor: string | null,
		readonly nextCursor: string | null,
		readonly previousCursor: string | null,
		readonly filterModel: GridFilterModel,
		readonly sortModel: GridSortModel | null,
		readonly columns: GraphQlGridColDef[] | null,
		readonly hasNextPage: boolean,
		readonly loadedPage: number | undefined
	) {
	}


	computeGraphQlFilter() {
		if (this.filterModel == null) return null;

		const entries = this.filterModel?.items
			.map((it) => {

				const col = this.columns?.filter(x => x.field === it.field)[0];

				let filterOption;
				const filterValue: any =
					!col || !it.value ? it.value :
						col.type === "number" ? parseInt(it.value) :
							col.type === "boolean" ? (typeof it.value ==="boolean"? it.value: it.value.toLowerCase() === "true") :
								col.type === "dateTime" ? toGraphQlDateTime(it.value) :
									it.value;

				switch (it.operator) {
					case "contains":
						if (it.value) filterOption = col?.filterIgnoreCase ? {containsIgnoreCase: filterValue} : {contains: filterValue};
						break;
					case "equals":
						if (it.value) filterOption = col?.filterIgnoreCase ? {eqIgnoreCase: filterValue} : {eq: filterValue};
						break;
					case "is":
						if (it.value) filterOption = col?.filterIgnoreCase ? {eqIgnoreCase: filterValue} : {eq: filterValue};
						break;
					case "not":
					case "!=":
						if (it.value) filterOption = col?.filterIgnoreCase ? {neqIgnoreCase: filterValue} : {neq: filterValue};
						break;
					case "startsWith":
						if (it.value) filterOption = col?.filterIgnoreCase ? {startsWithIgnoreCase: filterValue} : {startsWith: filterValue};
						break;
					case "endsWith":
						if (it.value) filterOption = col?.filterIgnoreCase ? {endsWithIgnoreCase: filterValue} : {endsWith: filterValue};
						break;
					case "isEmpty":
						filterOption = {eq: null};
						break;
					case "isNotEmpty":
						filterOption = {neq: null};
						break;
					case "isAnyOf":
						if (filterValue && filterValue.length > 0) filterOption = {in: filterValue};
						break;
					case "before":
					case "<":
						if (it.value) filterOption = {lt: filterValue};
						break;
					case "onOrBefore":
					case "<=":
						if (it.value) filterOption = {lte: filterValue};
						break;
					case "after":
					case ">":
						if (it.value) filterOption = {gt: filterValue};
						break;
					case "onOrAfter":
					case ">=":
						if (it.value) filterOption = {gte: filterValue};
						break;
				}

				if (!filterOption) return null;

				if (col?.filterAction) {
					return col.filterAction(filterOption);
				}

				return unflattenObject({
					[it.field]: filterOption,
				});
			})
			.filter((it) => !!it);

		if (entries == null || entries.length === 0) return {};

		if (entries.length === 1) return entries[0];

		return {[this.filterModel.logicOperator ?? "and"]: entries};
	}

	computeGraphQlSort() {
		if (this.sortModel == null || this.sortModel.length === 0) return null;
		return this.sortModel.map((it) => {
			return unflattenObject({[it.field]: it.sort?.toUpperCase()});
		});
	}
}

export interface GraphQlDataGridProps {
	id?: string|number;
	query: DocumentNode;
	graphFunction: string;
	client: ApolloClient<any>;
	rowsPerPageOptions: number[];
	initialPageSize: number;
	itemConverter?: (item: any) => any;
	defaultFilterModel?: GridFilterModel;
	defaultSortModel?: GridSortModel;
	getRowId?: (item: any) => any;
	readonly queryVariables?: object;
	back?: () => void;
	customButtons?: React.ReactNode[];
	onDataFetched?: (items: any[]) => void;
	enableHeaderFilter?: boolean;
	enableToolbarFilter?: boolean;
	columns: GraphQlGridColDef[];
	onDataRefresh?: () => void;
	/**
	 * true to enable InfiteLoading. Also set the GridHeight te enable this feature
	 * @default false
	 */
	infiniteLoading?: boolean;
	gridHeight?: number;
	toolbarElements?: ReactNode;
	autoSelectSingleRecord?: boolean;
	onAutoSelect?: (row: any) => void;
	hideExportButton?: boolean;
	persistFilters?: boolean;
	persistSorting?: boolean;
}

interface PageInfo {
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	startCursor: string | null;
	endCursor: string | null;
}

function identity<T>(it: T) {
	return it;
}

export function nestedValueGetter(params: GridValueGetterParams): any {
	const keys = params.field.split(".");
	let value = params.row;

	for (const key of keys) {
		value = value?.[key];
		if (value === undefined) break; // Stop if any level is undefined
	}

	return value;
}

export type GraphQlDataGridHandle = {
	graphQlWhereClause: () => any;
};

export const GraphQlDataGrid = forwardRef<GraphQlDataGridHandle, GraphQlDataGridProps & DataGridProProps>((props, ref) => {
	const storedFilter = localStorageOfObject(`graphqldatagrid:${props.id}.state:filter`, true);
	const storedSort = localStorageOfObject(`graphqldatagrid:${props.id}.state:sort`, true);
	const [state, setState] = useState(() => GraphQlDataGridState.default(props, props.persistFilters ? storedFilter.get() : undefined, props.persistSorting?  storedSort.get(): undefined));
	const autoSelect = useRef<boolean | undefined>(props.autoSelectSingleRecord);
	const nodeList = useRef<any[]>([]);
	const [paginationModel, setPaginationModel] = React.useState({
		pageSize: state.pageSize,
		page: state.currentPage,
	});

	if (props.id === undefined && (props.persistSorting || props.persistFilters)) {
		throw new Error("For persisting Sort or Filters the id property is required.");
	}
	const computedFilter = state.computeGraphQlFilter();
	const queryVariablesWhere = (props.queryVariables as any)?.where;

	let mergedWhere = computedFilter;
	if (queryVariablesWhere && Object.keys(queryVariablesWhere).length > 0) {
		if (Object.keys(computedFilter).length > 0) {
			mergedWhere = { and: [queryVariablesWhere, computedFilter] };
		} else {
			mergedWhere = queryVariablesWhere;
		}
	}

	// The merged clause, not the column filters alone: the intake and auction reports keep their bulk
	// email exclusion in queryVariables.where, and their "Send Emails" button re-collects rows through
	// this handle - so returning only the column filters would mail exactly the addresses the operator
	// excluded, while the grid in front of them showed those rows as gone.
	useImperativeHandle(ref, () => ({
		graphQlWhereClause() {
			return mergedWhere;
		}
	}));

	function resetNodeList() {
		if (nodeList.current.length !== 0) {
			nodeList.current = [];
		}
	}

	const {loading, error, data, refetch} = useQuery(props.query, {
		client: props.client,
		fetchPolicy: "no-cache",
		variables: {
			...props.queryVariables,
			first: !state.beforeCursor ? state.pageSize : null,
			last: state.beforeCursor ? state.pageSize : null,
			after: state.afterCursor,
			take: state.pageSize,
			skip: state.pageSize * (state.currentPage + state.offsetPage),
			before: state.beforeCursor,
			where: mergedWhere,
			order: state.computeGraphQlSort(),
		},
	});


	let totalCount;
	let totalCountPostfix;
	if (loading || error) {
		//nodes = [];
		totalCount = 0;
		totalCountPostfix = "'+'";
	} else {
		const result = data[props.graphFunction];
		const items = result.nodes ?? result.items;
		let pageInfo: PageInfo = result.pageInfo;
		
		if (pageInfo == null) {
			pageInfo = {
				hasNextPage: false,
				endCursor: null,
				hasPreviousPage: false,
				startCursor: null
			};
		}
		
		if (pageInfo.hasNextPage) {
			totalCount = (state.currentPage + state.offsetPage + 1) * state.pageSize + 1;
			totalCountPostfix = "'+'";
		} else {
			totalCount = (state.currentPage + state.offsetPage) * state.pageSize + items.length;
			totalCountPostfix = "''";
		}

		const loadedPage = state.loadedPage;
		if (loadedPage === undefined || loadedPage !== state.currentPage + state.offsetPage) {
			setState(
				new GraphQlDataGridState(
					state.options,
					state.pageSize,
					state.currentPage,
					state.offsetPage,
					state.afterCursor,
					state.beforeCursor,
					pageInfo.endCursor,
					pageInfo.startCursor,
					state.filterModel,
					state.sortModel,
					state.columns,
					pageInfo.hasNextPage,
					state.currentPage + state.offsetPage
				)
			);
			if (!props.infiniteLoading) {
				nodeList.current = items.map(props.itemConverter ?? identity);
			} else {
				nodeList.current = [...nodeList.current, ...items.map(props.itemConverter ?? identity)];
			}
		}
	}
	

	
	useEffect(() => {
		if (props.persistFilters) {
			storedFilter.set(state.filterModel.items);
		}
	}, [state.filterModel, props.persistFilters, storedFilter]);
	
	useEffect(() => {
		if (props.persistSorting) {
			storedSort.set(state.sortModel);
		}
	}, [state.sortModel, props.persistSorting, storedSort]);
	
	useEffect(() => {
		resetNodeList();
		setState(
			new GraphQlDataGridState(
				state.options,
				state.pageSize,
				0, // reset to page 0
				0,
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				state.filterModel,
				state.sortModel,
				state.columns,
				state.hasNextPage,
				undefined
			));
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.queryVariables]);

	useEffect(() => {
		const rows: any[] = nodeList.current;
		if (props.onDataFetched) {
			if (rows.length > 0) props.onDataFetched(rows);
		}
		if (autoSelect.current && rows.length === 1 && props.onAutoSelect) {
			props.onAutoSelect(rows[0]);
		}
		if (autoSelect.current && rows.length > 0) {
			autoSelect.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nodeList.current]);

	const handleOnRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = (params) => {
		if (state.hasNextPage && params.visibleRowsCount > 0) {
			setState(
				new GraphQlDataGridState(
					state.options,
					state.pageSize,
					state.currentPage,
					state.offsetPage + 1,
					state.nextCursor,
					state.beforeCursor,
					state.nextCursor,
					state.previousCursor,
					state.filterModel,
					state.sortModel,
					state.columns,
					state.hasNextPage,
					state.loadedPage
				)
			);
		}
	};
	// multi-column sort enabled in pro plan
	return (
		<>
			{error &&
				<Alert
					severity={"error"}>{error.message}{error?.graphQLErrors.map((it) => it.toString())}</Alert>
			}
			<div style={{height: props.gridHeight ?? "100%"}}>
				<StripedDataGrid
					onRowsScrollEnd={(props.infiniteLoading) ? handleOnRowsScrollEnd : undefined}
					//hideFooterPagination
					//localeText={nlNL.components.MuiDataGrid.defaultProps.localeText}
					localeText={GlobalState.userPreferences.languageKeyRequired === "nl-NL" ? nlNL.components.MuiDataGrid.defaultProps.localeText : undefined}
					disableRowSelectionOnClick
					disableMultipleRowSelection
					loading={loading}
					unstable_headerFilters={props.enableHeaderFilter}
					// idea taken from https://github.com/mui/mui-x/issues/409#issuecomment-1233333917
					sx={{
						".MuiTablePagination-displayedRows:after": {
							content: totalCountPostfix,
						},
						".MuiDataGrid-rowCount:after": {
							content: totalCountPostfix,
						},
					}}
					hideFooterPagination={props.infiniteLoading}
					hideFooterRowCount={false}
					pagination={!props.infiniteLoading}
					paginationMode="server"
					paginationModel={paginationModel}
					pageSizeOptions={state.options.rowsPerPageOptions}
					onPaginationModelChange={handlePaginationModelChange}
					rowCount={totalCount}
					filterMode="server"
					filterModel={state.filterModel}
					onFilterModelChange={onFilterChange}
					disableColumnFilter={false}
					sortingMode="server"
					sortModel={state.sortModel ?? undefined}
					onSortModelChange={onSortModelChange}
					autoHeight={props.infiniteLoading ? false : props.autoHeight} // autoHeight breaks infiniteLoading

					{...props}
					rows={nodeList.current}
					slots={{
						...props.slots,
						toolbar: ToolBar,
						loadingOverlay: LinearProgress,
					}}


					getRowId={props.getRowId}
					slotProps={{
						...props.componentsProps,
						toolbar: {
							...props.componentsProps?.toolbar,
							gridProps: props,
							refetch,
							state,
							onFilterChange,
							onRefresh
						},
						pagination: {
							labelRowsPerPage: translate("Rows per page:"),
							...props.componentsProps?.pagination,
						}
					}}
				/>
			</div>
		</>
	);

	function handlePaginationModelChange(newPaginationModel: GridPaginationModel) {
		const currentPage = state.currentPage;
		const pageSize = state.pageSize;
		setPaginationModel(newPaginationModel);
		resetNodeList();
		// We have the cursor, we can allow the page transition.
		if (newPaginationModel.page !== currentPage) {
			onPageChange(newPaginationModel.page, currentPage);
		}
		if (newPaginationModel.pageSize !== pageSize) {
			onPageSizeChange(newPaginationModel.pageSize);
		}

	}

	function onPageChange(page: number, previousPage: number) {
		resetNodeList();
		if (page > previousPage)
			setState(
				new GraphQlDataGridState(
					state.options,
					state.pageSize,
					page, // use new page
					0,
					state.nextCursor, // use next cursor
					null,
					null, // reset cursor
					null, // reset cursor
					state.filterModel,
					state.sortModel,
					state.columns,
					state.hasNextPage,
					undefined
				)
			);
		else
			setState(
				new GraphQlDataGridState(
					state.options,
					state.pageSize,
					page, // use new page
					0,
					null,
					state.previousCursor, // use previous cursor
					null, // reset cursor
					null, // reset cursor
					state.filterModel,
					state.sortModel,
					state.columns,
					state.hasNextPage,
					undefined
				)
			);
	}

	function onPageSizeChange(pageSize: number) {
		resetNodeList();
		setState(
			new GraphQlDataGridState(
				state.options,
				pageSize, // use new page size
				0, // reset to page 0
				0,
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				state.filterModel,
				state.sortModel,
				state.columns,
				state.hasNextPage,
				undefined
			)
		);
	}

	function onFilterChange(model: GridFilterModel) {
		resetNodeList();
		setState(
			new GraphQlDataGridState(
				state.options,
				state.pageSize,
				0, // reset to page 0
				0,
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				model,
				state.sortModel,
				state.columns,
				state.hasNextPage,
				undefined
			)
		);
	}

	function onRefresh() {
		resetNodeList();
		setState(
			new GraphQlDataGridState(
				state.options,
				state.pageSize,
				0, // reset to page 0
				0,
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				state.filterModel,
				state.sortModel,
				state.columns,
				state.hasNextPage,
				undefined
			)
		);
	}

	function onSortModelChange(model: GridSortModel) {
		resetNodeList();
		setState(
			new GraphQlDataGridState(
				state.options,
				state.pageSize,
				0, // reset to page 0
				0,
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				null, // reset cursor
				state.filterModel,
				model,
				state.columns,
				state.hasNextPage,
				undefined
			)
		);
	}
});

interface ToolBarProps {
	readonly gridProps: GraphQlDataGridProps & DataGridProProps;
	readonly refetch: () => void;
	readonly state: GraphQlDataGridState;
	readonly onFilterChange: (model: GridFilterModel) => void;
	readonly onRefresh: () => void;
}

function ToolBar(props: ToolBarProps) {
	return <>
		<Box sx={{display: "flex", justifyContent: "space-between", p: "5px"}}>
			<Box sx={{display: "flex", justifyContent: "space-between", p: "5px"}}>
				{props.gridProps.toolbarElements}
				{props.gridProps.back && (
					<IconButton onClick={() => props.gridProps.back?.call(undefined)}>
						<ArrowBack/>
					</IconButton>
				)}
				{props.gridProps.customButtons &&
					props.gridProps.customButtons.map((it) => {
						return it;
					})}
			</Box>
			<div>
				<IconButton onClick={() => {
					if (props.gridProps.onDataRefresh)
						props.gridProps.onDataRefresh();
					props.refetch();
					if (props.onRefresh) {
						props.onRefresh();
					}
				}}>
					<Refresh/>
				</IconButton>
			</div>
		</Box>
		{(props.gridProps.enableToolbarFilter ?? !props.gridProps.enableHeaderFilter) &&
			<GraphQlDataGridFilter model={props.state.filterModel} columns={props.gridProps.columns}
				onChange={props.onFilterChange}/>
		}
		{!props.gridProps.hideExportButton &&
			<div>
				<GridToolbarExport columns={props.gridProps.columns} printOptions={{disableToolbarButton: true}}/>
			</div>
		}
	</>;
}
